'use client';

import { useState, useEffect } from 'react';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterConfig {
  key: string;
  label: string;
  options: FilterOption[];
}

export interface TableToolbarProps {
  search?: string;
  onSearchChange?: (value: string) => void;
  filters?: FilterConfig[];
  activeFilters?: Record<string, string>;
  onFilterChange?: (key: string, value: string) => void;
  placeholder?: string;
  dateRange?: {
    startDate: string;
    endDate: string;
    onStartDateChange: (value: string) => void;
    onEndDateChange: (value: string) => void;
  };
}

export function TableToolbar({
  search = '',
  onSearchChange,
  filters = [],
  activeFilters = {},
  onFilterChange,
  placeholder = 'Search...',
  dateRange,
}: TableToolbarProps) {
  const [localSearch, setLocalSearch] = useState(search);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== search && onSearchChange) {
        onSearchChange(localSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch, search, onSearchChange]);

  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  const hasActiveFilters = Object.keys(activeFilters).length > 0 || localSearch.length > 0 || (dateRange && (dateRange.startDate || dateRange.endDate));

  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Search Input */}
      <div className="relative flex-1">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
        />
        {localSearch && (
          <button onClick={() => { setLocalSearch(''); onSearchChange?.(''); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        )}
      </div>

      {/* Filters & Date Range */}
      <div className="flex flex-wrap gap-2 items-end">
        {filters.map((filter) => (
          <select
            key={filter.key}
            value={activeFilters[filter.key] || ''}
            onChange={(e) => onFilterChange?.(filter.key, e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer"
          >
            <option value="">{filter.label}</option>
            {filter.options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ))}

        {dateRange && (
          <div className="flex items-center gap-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5">
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => dateRange.onStartDateChange(e.target.value)}
              className="bg-transparent text-sm text-gray-900 dark:text-gray-100 outline-none"
              title="Start Date"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => dateRange.onEndDateChange(e.target.value)}
              className="bg-transparent text-sm text-gray-900 dark:text-gray-100 outline-none"
              title="End Date"
            />
            {(dateRange.startDate || dateRange.endDate) && (
              <button
                onClick={() => { dateRange.onStartDateChange(''); dateRange.onEndDateChange(''); }}
                className="text-gray-400 hover:text-red-500 transition-colors"
                title="Clear dates"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            )}
          </div>
        )}

        {hasActiveFilters && (
          <button
            onClick={() => {
              setLocalSearch('');
              onSearchChange?.('');
              if (onFilterChange) Object.keys(activeFilters).forEach((key) => onFilterChange(key, ''));
              if (dateRange) { dateRange.onStartDateChange(''); dateRange.onEndDateChange(''); }
            }}
            className="px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
